import { Stack, Typography, useTheme } from '@mui/material';
import {
  CSSProperties,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';

import { CheckIcon } from '@/components/icons/CheckIcon';
import { Copy01Icon } from '@/components/icons/Copy01Icon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import {
  ButtonBar,
  ButtonBarItem,
} from '@/components/layout/ButtonBar/ButtonBar';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useFormContext } from '@/components/react-hook-form';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { getNewBundleId } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import { COLORS } from '@/styles/tokens/colors';
import { PresentationBundleKind } from '@/types/schema';

import {
  BORDER_COLOR,
  BORDER_COLOR_WARNING,
} from '../../../PresentationTemplateEditor.constants';
import {
  useBundleKindRequiresConfigModal,
  usePresentationTemplateEditorContext,
} from '../../../PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
  PresentationTemplateEditorStage,
} from '../../../PresentationTemplateEditor.types';
import { AdditionalPromptContextModal } from './AdditionalPromptContextModal';
import {
  getBundleTileDefaultIcon,
  getBundleTileHasMoreInformationRequired,
  getBundleTileIcon,
  getBundleTileLabels,
  getBundleTileMultiPageIcon,
  isBundlePageEditable,
} from './PresentationTemplateBundleTile.utils';

/**
 * Bundle kinds whose AI prompt the firm can extend from the template editor.
 * The server reads additional prompt context only for these kinds.
 */
const BUNDLE_KINDS_WITH_ADDITIONAL_PROMPT_CONTEXT: PresentationBundleKind[] = [
  PresentationBundleKind.DiagramBundle,
];

function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

export interface PresentationTemplateStageEditorBundleTileProps {
  index: number;
}

export function PresentationTemplateBundleTile({
  index,
}: PresentationTemplateStageEditorBundleTileProps) {
  const innerProps = useInnerProps({ index });

  if (!innerProps) {
    return <div>TODO missing bundle</div>;
  }

  return <BundleTileInner {...innerProps} />;
}

function useInnerProps({
  index,
}: PresentationTemplateStageEditorBundleTileProps): BundleTileInnerProps | null {
  const { control } = useFormContext<PresentationTemplateEditorFormShape>();
  const [bundle] = useWatch({ control, name: [`bundles.${index}`] });
  const bundleKindRequiresConfigModal = useBundleKindRequiresConfigModal();

  return useMemo<BundleTileInnerProps | null>(() => {
    if (!bundle) return null;

    const { label, subLabel } = getBundleTileLabels(bundle);
    // A bundle is configurable if it uses the page editor or if its modal
    // is enabled (feature-flag-gated for some bundle kinds).
    const isConfigurable =
      isBundlePageEditable(bundle) ||
      bundleKindRequiresConfigModal(bundle.kind);

    // Only show the "review and edit" warning for configurable bundles —
    // if the bundle's modal is gated behind a disabled flag, there's
    // nothing for the user to act on.
    const hasMoreInformationRequired =
      isConfigurable && getBundleTileHasMoreInformationRequired(bundle);
    const icon = hasMoreInformationRequired
      ? getBundleTileIcon(bundle)
      : getBundleTileDefaultIcon(bundle);

    const borderColor = hasMoreInformationRequired
      ? BORDER_COLOR_WARNING
      : BORDER_COLOR;

    return {
      index,
      bundle,
      icon,
      label,
      subLabel,
      hasMoreInformationRequired,
      borderColor,
      isConfigurable,
      labelIcon: getBundleTileMultiPageIcon(bundle),
    };
  }, [index, bundle, bundleKindRequiresConfigModal]);
}

interface BundleTileInnerProps {
  index: number;
  bundle: PresentationTemplateEditorBundle;
  icon: ReactNode;
  label: string;
  subLabel: string | null;
  hasMoreInformationRequired: boolean;
  borderColor: string;
  isConfigurable: boolean;
  labelIcon: ReactNode;
}

function BundleTileInner({
  index,
  bundle,
  icon,
  label,
  subLabel,
  borderColor,
  isConfigurable,
  labelIcon,
}: BundleTileInnerProps) {
  const {
    bundlesFormArray: { insert, remove, update },
    setStage,
    openBundleConfigModal,
    markBundlesDirty,
  } = usePresentationTemplateEditorContext();
  const bundleKindRequiresConfigModal = useBundleKindRequiresConfigModal();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // The overflow menu renders in a portal, so opening it and moving the pointer
  // onto the menu leaves the tile. Pin the hover bar until the menu closes,
  // otherwise it unmounts and takes the open menu with it.
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [isPromptContextOpen, setIsPromptContextOpen] = useState(false);
  const supportsAdditionalPromptContext =
    BUNDLE_KINDS_WITH_ADDITIONAL_PROMPT_CONTEXT.includes(bundle.kind);
  const additionalPromptContext = bundle.additionalPromptContext ?? '';

  const handleEdit = useCallback(() => {
    if (bundleKindRequiresConfigModal(bundle.kind)) {
      openBundleConfigModal(bundle.kind, index);
      return;
    }
    setStage(PresentationTemplateEditorStage.Page, bundle.id);
  }, [
    bundle.id,
    bundle.kind,
    index,
    openBundleConfigModal,
    setStage,
    bundleKindRequiresConfigModal,
  ]);

  const handleDuplicate = useCallback(() => {
    insert(index + 1, { ...bundle, id: getNewBundleId(bundle.kind) });
  }, [insert, index, bundle]);

  const handleDeleteClick = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleClick = useCallback(() => {
    if (bundleKindRequiresConfigModal(bundle.kind)) {
      openBundleConfigModal(bundle.kind, index);
      return;
    }
    setStage(PresentationTemplateEditorStage.Page, bundle.id);
  }, [
    bundle.id,
    bundle.kind,
    index,
    openBundleConfigModal,
    setStage,
    bundleKindRequiresConfigModal,
  ]);

  const isExcludedFromOutput = bundle.excludeFromOutput ?? false;

  const handleToggleExcludeFromOutput = useCallback(() => {
    update(index, { ...bundle, excludeFromOutput: !isExcludedFromOutput });
    // useFieldArray.update() doesn't propagate dirty state to the parent form,
    // so flip isDirty explicitly to keep the Save button enabled.
    markBundlesDirty();
  }, [update, index, bundle, isExcludedFromOutput, markBundlesDirty]);

  const handleSaveAdditionalPromptContext = useCallback(
    (value: string | null) => {
      update(index, { ...bundle, additionalPromptContext: value });
      markBundlesDirty();
      setIsPromptContextOpen(false);
    },
    [update, index, bundle, markBundlesDirty]
  );

  const buttonBarItems = useMemo<ButtonBarItem[]>(() => {
    const items: ButtonBarItem[] = [];
    if (isConfigurable) {
      items.push({ icon: Edit02Icon, label: 'Edit', onClick: handleEdit });
    }
    items.push({
      icon: Copy01Icon,
      label: 'Duplicate',
      onClick: handleDuplicate,
    });
    items.push({
      label: 'More options',
      onMenuOpenChange: setIsOverflowMenuOpen,
      menu: (
        <>
          <MenuItem
            label="Exclude from generated output"
            onClick={handleToggleExcludeFromOutput}
            iconAfter={
              isExcludedFromOutput && (
                <CheckIcon
                  size={16}
                  color={COLORS.FUNCTIONAL.SUCCESS.DEFAULT}
                />
              )
            }
            muiMenuItemProps={{ selected: isExcludedFromOutput }}
          />
          {supportsAdditionalPromptContext && (
            <MenuItem
              label="Add custom refinement prompt"
              onClick={() => setIsPromptContextOpen(true)}
              iconAfter={
                additionalPromptContext && (
                  <CheckIcon
                    size={16}
                    color={COLORS.FUNCTIONAL.SUCCESS.DEFAULT}
                  />
                )
              }
            />
          )}
        </>
      ),
    });
    items.push({
      icon: Trash01Icon,
      label: 'Delete',
      onClick: handleDeleteClick,
      iconProps: { color: COLORS.FUNCTIONAL.ERROR.DEFAULT },
    });
    return items;
  }, [
    isConfigurable,
    handleEdit,
    handleDuplicate,
    handleDeleteClick,
    handleToggleExcludeFromOutput,
    isExcludedFromOutput,
    supportsAdditionalPromptContext,
    additionalPromptContext,
  ]);

  return (
    <>
      <PresentationBundleTileBody
        borderColor={borderColor}
        isConfigurable={isConfigurable}
        icon={icon}
        label={label}
        subLabel={subLabel}
        onClick={handleClick}
        buttonBarItems={buttonBarItems}
        labelIcon={labelIcon}
        isButtonBarPinned={isOverflowMenuOpen}
        previewBadge={
          supportsAdditionalPromptContext &&
          additionalPromptContext && (
            <Tooltip title={additionalPromptContext}>
              <span>
                <Badge
                  variant={BadgeVariants.Accent}
                  display="Modified prompt"
                />
              </span>
            </Tooltip>
          )
        }
      />
      {/*
        The tile's modals render in portals, but React events still bubble
        through the component tree to the dnd-kit listeners on the sortable
        wrapper in DraggableBundleTile. Without this boundary, Space in a modal
        starts a keyboard drag (which also swallows the keystroke), and pointer
        input in a modal can reorder the slides behind it.
      */}
      <div
        style={{ display: 'contents' }}
        onKeyDown={stopPropagation}
        onPointerDown={stopPropagation}
      >
        {isPromptContextOpen && (
          <AdditionalPromptContextModal
            initialValue={additionalPromptContext}
            onClose={() => setIsPromptContextOpen(false)}
            onSave={handleSaveAdditionalPromptContext}
          />
        )}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          heading="Are you sure you want to remove this slide?"
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonProps={{ variant: 'destructive-primary' }}
          onClose={(result) => {
            if (result === 'confirm') {
              remove(index);
            }
            setIsDeleteModalOpen(false);
          }}
        />
      </div>
    </>
  );
}

export interface PresentationBundleTileBodyProps {
  borderColor: CSSProperties['borderColor'];
  isConfigurable?: boolean;
  icon: ReactNode;
  label: ReactNode;
  /** Optional subtext below the label, e.g. a divider's configured title. */
  subLabel?: string | null;
  onClick: () => void;
  buttonBarItems?: ButtonBarItem[];
  labelIcon?: ReactNode;
  /**
   * Keeps the hover button bar mounted while not hovered. Needed when one of
   * its items owns an open popover that would otherwise be torn down with it.
   */
  isButtonBarPinned?: boolean;
  /** Optional badge pinned to the bottom-left corner of the preview box. */
  previewBadge?: ReactNode;
}

export function PresentationBundleTileBody({
  borderColor,
  isConfigurable = false,
  icon,
  label,
  subLabel,
  buttonBarItems,
  onClick: onClick,
  labelIcon,
  isButtonBarPinned = false,
  previewBadge,
}: PresentationBundleTileBodyProps) {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Stack
      direction="column"
      width="208px"
      spacing={1.5}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack
        direction="column"
        width="100%"
        height="150px"
        p={1}
        border={`1px solid ${borderColor}`}
        borderRadius={1}
        position="relative"
        justifyContent="center"
        alignItems="center"
        onClick={isConfigurable ? onClick : undefined}
        sx={{
          cursor: isConfigurable ? 'pointer' : undefined,
          ':hover': {
            boxShadow: theme.palette.shadows.xs,
          },
        }}
      >
        {icon}
        {previewBadge && (
          <Stack position="absolute" bottom={8} left={8}>
            {previewBadge}
          </Stack>
        )}
        {(isHovered || isButtonBarPinned) && buttonBarItems && (
          <ButtonBar
            items={buttonBarItems}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        {labelIcon}
        <Stack direction="column" spacing={0.25} minWidth={0}>
          <Typography variant="body-xs" color="textPrimary">
            {label}
          </Typography>
          {subLabel && (
            // `body-xs` defaults to the muted secondary text color, which is
            // what distinguishes the subtext from the label above it.
            <TypographyClamped
              variant="body-xs"
              lines={2}
              tooltipProps={{ title: subLabel }}
            >
              {subLabel}
            </TypographyClamped>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
